import {useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useDecisionCallback} from '@s-ui/pde'
import AtomButton, {
  atomButtonAlignment,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {
  MoleculeDrawer,
  moleculeDrawerAnimationDuration,
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import {useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft'
import IconArrowRight from '@adv-mt/mt-svg-icons/lib/ArrowRightMedium.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import TitleBasic, {titleBasicSizes} from '@adv-mt/mt-title-basic'

import {ReservationInfo} from './reservation-info/reservation-info.js'
import {ReservationInfoRevamp} from './reservation-info/reservation-info-revamp.js'
import {ReservationLegalConditions} from './reservation-legal-conditions/reservation-legal-conditions.js'
import {ReservationTerms} from './reservation-terms/reservation-terms.js'
import {CONTENT_TYPE} from './utils/config.js'
import {useTracking} from './utils/useTracking.js'

const altTextThirdParty = `apple pay google pay mastercard visa`

export default function DrawerReservationInitialization({
  adInfo = {},
  onCloseDrawer,
  isDrawerOpen,
  drawerContent = CONTENT_TYPE.RESERVATION_INFO,
  handleLinkInfoClick = () => {},
  handleLegalConditionsClick = () => {},
  hitOrigin,
  showFooter = true,
  isNewReservationModuleV3 = false
}) {
  const {domain, i18n} = useContext(Context)
  const {decide} = useDecisionCallback()
  const documentationDrawerRef = useRef()
  const hasDecidedReservationFlowRevamp = useRef(false)
  const [currentContent, setCurrentContent] = useState(drawerContent)
  const [contentHistory, setContentHistory] = useState([])
  const [isReservationFlowRevamp, setIsReservationFlowRevamp] = useState(false)

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const isWiderThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const logoThirdParty = `${URL_STATICS_IMAGES}coches/transaction/apple-pay-google-pay-credit-cards.svg`

  const {triggerLoginModal} = useContext(LoginModalContext)
  const {isLogged, user} = useContext(AuthenticationContext)
  const router = useRouter()
  const {
    trackHandleButtonOnCkick,
    trackHandleNavigateToTerms,
    trackHandleNavigateToTermsBottomBar,
    trackHandleNavigateToLegalConditions,
    trackHandleOnClose,
    trackReservationDrawerViewed
  } = useTracking()

  const {RESERVATION_PAYMENT_PATH_PATTERN} = config.get('page')
  const {LOGIN_SOURCE_ID_RESERVATIONS} = config.get('login')
  const {VERIFICATION_REMINDER: STEP_VERIFICATION_REMINDER} = config.get('LOGIN_STEPS')
  const {HIT_ORIGINS} = config.get('tracking')

  const redirectToTermsPage = () => router.push(`${RESERVATION_PAYMENT_PATH_PATTERN}?adId=${adInfo.id}`)

  const handleButtonOnClick = async () => {
    trackHandleButtonOnCkick({adInfo, currentContent, hitOrigin})

    if (!isLogged || user.isEmailConfirmed === false) {
      onCloseDrawer()
      triggerLoginModal({
        modalProps: {
          adId: String(adInfo.id),
          source: LOGIN_SOURCE_ID_RESERVATIONS,
          trackingOrigin: HIT_ORIGINS.RESERVATION,
          onLogin: redirectToTermsPage,
          enableVerificationReminder: true,
          initialStep: isLogged && user.isEmailConfirmed === false ? STEP_VERIFICATION_REMINDER : undefined
        }
      })
      return
    }
    redirectToTermsPage()
  }

  const handleNavigateToTerms = () => {
    trackHandleNavigateToTerms(adInfo)
    navigateToContent(CONTENT_TYPE.RESERVATION_TERMS)
  }

  const handleNavigateToTermsBottomBar = () => {
    trackHandleNavigateToTermsBottomBar(adInfo)
    navigateToContent(CONTENT_TYPE.RESERVATION_TERMS)
  }

  const handleNavigateToLegalConditions = () => {
    trackHandleNavigateToLegalConditions(adInfo)
    navigateToContent(CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS)
  }

  const handleClose = () => {
    if (currentContent === CONTENT_TYPE.RESERVATION_TERMS) {
      trackHandleOnClose()
    }
    setContentHistory([])
    onCloseDrawer()
  }

  const handleBack = e => {
    e.preventDefault()
    e.stopPropagation()
    if (contentHistory.length > 0) {
      const previousContent = contentHistory[contentHistory.length - 1]
      const newHistory = contentHistory.slice(0, -1)
      setContentHistory(newHistory)
      setCurrentContent(previousContent)
    }
  }

  const navigateToContent = content => {
    setContentHistory(prev => [...prev, currentContent])
    setCurrentContent(content)
  }

  const getContent = () => {
    switch (currentContent) {
      case CONTENT_TYPE.RESERVATION_INFO:
        if (isReservationFlowRevamp) {
          return {
            children: <ReservationInfoRevamp adInfo={adInfo} logoThirdParty={logoThirdParty} />,
            titleNode: (
              <div className="mt-DrawerReservationInitialization-titleRevamp">
                <p className="mt-DrawerReservationInitialization-titleRevampLine">
                  {i18n.t('VEHICLE_RESERVATION.MODAL_ONBOARDING.TITLE_REVAMP_LINE1')}
                </p>
                <p className="mt-DrawerReservationInitialization-titleRevampLine">
                  {i18n.t('VEHICLE_RESERVATION.MODAL_ONBOARDING.TITLE_REVAMP_LINE2')}
                </p>
              </div>
            )
          }
        }
        return {
          children: <ReservationInfo adInfo={adInfo} handleLinkInfoClick={handleNavigateToTerms} />,
          title: i18n.t('VEHICLE_RESERVATION.ONBOARDING_TITLE')
        }
      case CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS:
        return {
          children: <ReservationLegalConditions />,
          title: i18n.t('VEHICLE_RESERVATION.LEGAL_TITLE')
        }
      case CONTENT_TYPE.RESERVATION_TERMS:
        return {children: <ReservationTerms />, title: i18n.t('VEHICLE_RESERVATION.TERMS_TITLE')}
      default:
        return {children: null, title: ''}
    }
  }

  useEffect(() => {
    if (isDrawerOpen) {
      setCurrentContent(drawerContent)
      setContentHistory([])
    }
  }, [isDrawerOpen, drawerContent])

  // The experiment is exposed once the user is shown the reservation info, never on terms or legal conditions.
  // The drawer only opens on it from a reservable ad, so the audience attribute is always true.
  useEffect(() => {
    if (!isDrawerOpen || drawerContent !== CONTENT_TYPE.RESERVATION_INFO || hasDecidedReservationFlowRevamp.current) {
      return
    }
    hasDecidedReservationFlowRevamp.current = true
    const {enabled} = decide('ff_web_transaction_revamp_reservation_flow', {attributes: {isAdReservable: true}})
    setIsReservationFlowRevamp(enabled)
  }, [isDrawerOpen, drawerContent, decide])

  useEffect(() => {
    if (isDrawerOpen && drawerContent === CONTENT_TYPE.RESERVATION_INFO) {
      trackReservationDrawerViewed(adInfo)
    }
  }, [isDrawerOpen, drawerContent]) // eslint-disable-line react-hooks/exhaustive-deps

  const {children, title, titleNode} = getContent()
  const hasLegalConditionsContent = currentContent === CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS
  const showBackButton = contentHistory.length > 0
  const hasScrollableContent =
    currentContent === CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS || currentContent === CONTENT_TYPE.RESERVATION_TERMS

  const bodyClass = cx('mt-DrawerReservationInitialization-body', {
    'mt-DrawerReservationInitialization-body--scrollable': hasScrollableContent
  })

  const drawerElement = (
    <>
      <MoleculeDrawerOverlay isVisible={isDrawerOpen} />
      <MoleculeDrawer
        animationDuration={moleculeDrawerAnimationDuration.NORMAL}
        closeOnOutsideClick
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        placement={moleculeDrawerPlacements.RIGHT}
        ref={documentationDrawerRef}
        size={isWiderThanMobile ? moleculeDrawerSizes.AUTO : moleculeDrawerSizes.FILL}
      >
        <div className="mt-DrawerReservationInitialization-wrapper">
          <div className="mt-DrawerReservationInitialization-header">
            <div className="mt-DrawerReservationInitialization-headerActions">
              {showBackButton && (
                <div className="mt-DrawerReservationInitialization-backButton">
                  <AtomButton
                    design={atomButtonDesigns.FLAT}
                    leftIcon={
                      <AtomIcon as="button" size={ATOM_ICON_SIZES.medium}>
                        <IconAngleLeft />
                      </AtomIcon>
                    }
                    onClick={handleBack}
                    shape={atomButtonShapes.CIRCULAR}
                  />
                </div>
              )}
              <div className="mt-DrawerReservationInitialization-closeButton">
                <AtomButton
                  design={atomButtonDesigns.FLAT}
                  leftIcon={
                    <AtomIcon as="button" size={ATOM_ICON_SIZES.medium}>
                      <IconClose />
                    </AtomIcon>
                  }
                  onClick={handleClose}
                  shape={atomButtonShapes.CIRCULAR}
                />
              </div>
            </div>
            {titleNode || <TitleBasic size={titleBasicSizes.s} title={title} />}
          </div>
          <div className={bodyClass}>{children}</div>
          {showFooter && (
            <div className="mt-DrawerReservationInitialization-footer">
              {isReservationFlowRevamp && currentContent === CONTENT_TYPE.RESERVATION_INFO ? (
                <div className="mt-DrawerReservationInitialization-footerRevamp">
                  <AtomButton
                    design={atomButtonDesigns.OUTLINE}
                    onClick={handleNavigateToTermsBottomBar}
                    shape={atomButtonShapes.CIRCULAR}
                    size={atomButtonSizes.LARGE}
                  >
                    {i18n.t('VEHICLE_RESERVATION.MODAL_ONBOARDING.CTA_DOUBTS')}
                  </AtomButton>
                  <div className="mt-DrawerReservationInitialization-footerRevampCtaWrapper">
                    <AtomButton
                      design={atomButtonDesigns.SOLID}
                      onClick={handleButtonOnClick}
                      rightIcon={<IconArrowRight size={ATOM_ICON_SIZES.medium} />}
                      shape={atomButtonShapes.CIRCULAR}
                      size={atomButtonSizes.LARGE}
                      fullWidth
                    >
                      {i18n.t('VEHICLE_RESERVATION.MODAL_ONBOARDING.CTA_EXPERIMENT')}
                    </AtomButton>
                  </div>
                </div>
              ) : (
                <>
                  <AtomButton
                    design={atomButtonDesigns.SOLID}
                    onClick={handleButtonOnClick}
                    rightIcon={isNewReservationModuleV3 && <IconArrowRight size={ATOM_ICON_SIZES.medium} />}
                    shape={atomButtonShapes.CIRCULAR}
                    size={atomButtonSizes.LARGE}
                    fullWidth
                  >
                    {i18n.t(
                      isNewReservationModuleV3
                        ? 'VEHICLE_RESERVATION.MODAL_ONBOARDING.CTA_EXPERIMENT'
                        : 'VEHICLE_RESERVATION.MODAL_ONBOARDING.CTA'
                    )}
                  </AtomButton>
                  <div className="mt-DrawerReservationInitialization-footerInfo">
                    <div className="mt-DrawerReservationInitialization-paymentInfo">
                      <p className="mt-DrawerReservationInitialization-paymentText">{i18n.t('COMMON.PAY_WITH')}</p>
                      <img
                        className="mt-DrawerReservationInitialization-paymentLogos"
                        src={logoThirdParty}
                        alt={altTextThirdParty}
                      />
                    </div>
                    {!hasLegalConditionsContent && (
                      <AtomButton
                        alignment={atomButtonAlignment.LEFT}
                        design={atomButtonDesigns.LINK}
                        onClick={handleNavigateToLegalConditions}
                        size={atomButtonSizes.MEDIUM}
                      >
                        Condiciones legales
                      </AtomButton>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </MoleculeDrawer>
    </>
  )

  return typeof document !== 'undefined' ? createPortal(drawerElement, document.body) : null
}

DrawerReservationInitialization.displayName = 'DrawerReservationInitialization'
DrawerReservationInitialization.propTypes = {
  adInfo: PropTypes.object,
  onCloseDrawer: PropTypes.func,
  isDrawerOpen: PropTypes.bool,
  drawerContent: PropTypes.string,
  handleLinkInfoClick: PropTypes.func,
  handleLegalConditionsClick: PropTypes.func,
  hitOrigin: PropTypes.string,
  showFooter: PropTypes.bool,
  isNewReservationModuleV3: PropTypes.bool
}
